import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Filter,
  Users,
  Database,
  ChevronRight,
  ChevronLeft,
  X,
  Smile,
  Sparkles,
  ArrowLeft,
} from 'lucide-react';
import { getScans, formatScan, getAllChildClinics } from '../../api/loan-action-creator';
import CreditsLedger from './CreditsLedger';
import './scans.css';

const PAGE_SIZE = 10;

const statusFilterOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'completed', label: 'Completed' },
  { value: 'initiated', label: 'Initiated' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'failed', label: 'Failed' },
];

const scanTypeFilterOptions = [
  { value: '', label: 'All Scan Types' },
  { value: 'dental', label: 'Dental' },
];

const AVATAR_COLORS = [
  { bg: '#EDE9FE', text: '#6D28D9' },
  { bg: '#DBEAFE', text: '#1D4ED8' },
  { bg: '#FFEDD5', text: '#C2410C' },
  { bg: '#FCE7F3', text: '#BE185D' },
  { bg: '#D1FAE5', text: '#047857' },
  { bg: '#E0E7FF', text: '#4338CA' },
];

const getClinicCode = (clinic) =>
  clinic?.doctorCode || clinic?.merchantCode || clinic?.clinicCode || clinic?.clinic_code || '';

const getAvatarColor = (seed = '') => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (scan) => {
  const phone = (scan.patientPhone || '').replace(/\D/g, '');
  if (phone.length >= 2) return phone.slice(-2);
  const type = (scan.scanType || 'P').slice(0, 1).toUpperCase();
  return `${type}P`;
};

const formatStatusLabel = (status) => {
  if (!status || status === '—') return 'Unknown';
  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getStatusClass = (status) => {
  const normalized = (status || '').toLowerCase().replace(/\s+/g, '_');
  if (normalized === 'completed') return 'pl-status completed';
  if (normalized === 'failed') return 'pl-status failed';
  if (normalized === 'pending' || normalized === 'in_progress') return 'pl-status pending';
  return 'pl-status default';
};

const getAnalysisBadgeClass = (scanType) => {
  const type = (scanType || '').toLowerCase();
  if (type === 'dental') return 'pl-analysis-badge dental';
  if (type === 'skin') return 'pl-analysis-badge skin';
  if (type === 'hair') return 'pl-analysis-badge hair';
  return 'pl-analysis-badge default';
};

const formatAnalysisLabel = (scanType) => {
  if (!scanType || scanType === '—') return 'Analysis';
  return `${scanType.charAt(0).toUpperCase()}${scanType.slice(1)} Analysis`;
};

const getSeverityClass = (severity = '') => {
  const value = severity.toLowerCase();
  if (value.includes('attention') || value.includes('high') || value.includes('poor')) {
    return 'severity-attention';
  }
  if (value.includes('average') || value.includes('moderate')) {
    return 'severity-average';
  }
  if (value.includes('good') || value.includes('excellent')) {
    return 'severity-good';
  }
  return 'severity-default';
};

const ScoreBar = ({ label, score, notes }) => {
  const num = Number(score);
  const width = Number.isNaN(num) ? 0 : Math.max(0, Math.min(100, num));
  return (
    <div className="pl-score-item">
      <div className="pl-score-item-head">
        <span>{label}</span>
        <strong>{Number.isNaN(num) ? '—' : Math.round(num)}</strong>
      </div>
      <div className="pl-score-track">
        <div className="pl-score-fill" style={{ width: `${width}%` }} />
      </div>
      {notes && <p className="pl-score-notes">{notes}</p>}
    </div>
  );
};

const ScanDetailPanel = ({ scan, onClose }) => {
  if (!scan) return null;

  const result = scan.resultJson && typeof scan.resultJson === 'object' ? scan.resultJson : {};
  const avatar = getAvatarColor(scan.patientPhone || scan.id);
  const hasResults =
    (scan.resultScore !== '' && scan.resultScore != null) ||
    Boolean(result.summary) ||
    (scan.resultSummary && scan.resultSummary !== '') ||
    (Array.isArray(result.concerns) && result.concerns.length > 0);
  const scoreValue = parseFloat(scan.resultScore);
  const displayScore = Number.isFinite(scoreValue) ? Math.round(scoreValue) : null;
  const creditsValue = Number(scan.creditsCharged);
  const summaryText =
    result.summary ||
    scan.resultSummary ||
    (hasResults
      ? 'No summary available.'
      : 'This scan is still in progress. Score and analysis details will appear once it is completed.');

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="pl-detail-overlay" onClick={onClose}>
      <div
        className="pl-detail-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Scan details"
      >
        <div className="pl-detail-topbar">
          <button type="button" className="pl-detail-back" onClick={onClose}>
            <ArrowLeft size={18} />
            <span>Back</span>
          </button>
          <button type="button" className="pl-detail-close" onClick={onClose} aria-label="Close details">
            <X size={18} />
          </button>
        </div>

        <div className="pl-detail-header">
          <div className="pl-detail-patient">
            <div
              className="pl-avatar lg"
              style={{ backgroundColor: avatar.bg, color: avatar.text }}
            >
              {getInitials(scan)}
            </div>
            <div>
              <h3>{scan.patientPhone}</h3>
              <p>Clinic {scan.clinicCode}</p>
            </div>
          </div>
        </div>

        <div className="pl-detail-meta">
          <span className={getAnalysisBadgeClass(scan.scanType)}>
            <Smile size={12} />
            {formatAnalysisLabel(scan.scanType)}
          </span>
          <span className={getStatusClass(scan.status)}>{formatStatusLabel(scan.status)}</span>
          <span className="pl-detail-meta-item">Clinic {scan.clinicCode}</span>
          <span className="pl-detail-meta-item">
            Utilised {Number.isFinite(creditsValue) ? creditsValue : 0} credits
          </span>
          <span className="pl-detail-meta-item">
            {scan.datePart}{scan.timePart ? ` · ${scan.timePart}` : ''}
          </span>
        </div>

        <div className="pl-detail-body">
          <section className="pl-detail-section score-hero">
            <div className="pl-overall-score">
              <span className="pl-overall-value">{displayScore == null ? '—' : displayScore}</span>
              <span className="pl-overall-label">Score</span>
            </div>
            <p className="pl-summary-text">{summaryText}</p>
          </section>

          {!hasResults && (
            <section className="pl-detail-section">
              <div className="pl-pending-box">
                Status: <strong>{formatStatusLabel(scan.status)}</strong>
                <br />
                Analysis results are not available yet for this scan.
              </div>
            </section>
          )}

          {(result.whitenessScore != null ||
            result.alignmentScore != null ||
            result.imageQualityScore != null ||
            result.confidenceScore != null) && (
            <section className="pl-detail-section">
              <h4>Score Breakdown</h4>
              <div className="pl-score-grid">
                {result.whitenessScore != null && (
                  <ScoreBar
                    label="Whiteness"
                    score={result.whitenessScore}
                    notes={result.whitenessNotes}
                  />
                )}
                {result.alignmentScore != null && (
                  <ScoreBar
                    label="Alignment"
                    score={result.alignmentScore}
                    notes={result.alignmentNotes}
                  />
                )}
                {result.imageQualityScore != null && (
                  <ScoreBar
                    label="Image Quality"
                    score={result.imageQualityScore}
                    notes={result.imageQualityNotes}
                  />
                )}
                {result.confidenceScore != null && (
                  <ScoreBar label="Confidence" score={result.confidenceScore} />
                )}
              </div>
            </section>
          )}

          {Array.isArray(result.concerns) && result.concerns.length > 0 && (
            <section className="pl-detail-section">
              <h4>Concerns</h4>
              <div className="pl-concerns-list">
                {result.concerns.map((concern, idx) => (
                  <div key={`${concern.name}-${idx}`} className="pl-concern-card">
                    <div className="pl-concern-head">
                      <strong>{concern.name}</strong>
                      <span className={`pl-severity ${getSeverityClass(concern.severity)}`}>
                        {concern.severity || '—'}
                      </span>
                    </div>
                    <div className="pl-concern-score">Score: {concern.score ?? '—'}</div>
                    <p>{concern.description}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {Array.isArray(result.spacingIssues) && result.spacingIssues.length > 0 && (
            <section className="pl-detail-section">
              <h4>Spacing Issues</h4>
              <div className="pl-chip-list">
                {result.spacingIssues.map((issue) => (
                  <span key={issue} className="pl-chip">
                    {issue}
                  </span>
                ))}
              </div>
            </section>
          )}

          {result.recommendedTreatment && (
            <section className="pl-detail-section">
              <h4>Recommended Treatment</h4>
              <div className="pl-treatment-box">
                <Sparkles size={16} />
                <span>{result.recommendedTreatment}</span>
              </div>
            </section>
          )}

          {Array.isArray(result.recommendations) && result.recommendations.length > 0 && (
            <section className="pl-detail-section">
              <h4>Recommendations</h4>
              <ul className="pl-bullet-list">
                {result.recommendations.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          )}

          {Array.isArray(result.homeRemedies) && result.homeRemedies.length > 0 && (
            <section className="pl-detail-section">
              <h4>Home Remedies</h4>
              <ul className="pl-bullet-list">
                {result.homeRemedies.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>
          )}

          {scan.failureReason && (
            <section className="pl-detail-section">
              <h4>Failure Reason</h4>
              <p className="pl-failure-text">{scan.failureReason}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

const Scans = ({ initialTab = 'patients' }) => {
  const [activeTab, setActiveTab] = useState(initialTab === 'credits' ? 'credits' : 'patients');
  const [scans, setScans] = useState([]);

  useEffect(() => {
    if (initialTab === 'credits') setActiveTab('credits');
  }, [initialTab]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClinic, setSelectedClinic] = useState('');
  const [selectedClinicCode, setSelectedClinicCode] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedScanType, setSelectedScanType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [showClinicFilter, setShowClinicFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedScan, setSelectedScan] = useState(null);

  const doctorId = localStorage.getItem('doctorId');
  const defaultClinicCode = localStorage.getItem('merchantCode') || '';

  const fetchData = () => {
    setLoading(true);
    setError(null);

    const params = {};
    const clinicCode = selectedClinicCode || (!selectedClinic ? defaultClinicCode : '');
    if (clinicCode) params.clinic = clinicCode;
    if (selectedStatus) params.status = selectedStatus;
    if (selectedScanType) params.scan_type = selectedScanType;

    getScans(
      params,
      (data) => {
        setScans(data.results.map(formatScan));
        setLoading(false);
        setPage(1);
      },
      (err) => {
        setError(err.message || 'Failed to fetch scans');
        setScans([]);
        setLoading(false);
      }
    );
  };

  const fetchClinics = () => {
    getAllChildClinics(
      doctorId,
      (data) => {
        setClinics(data);
        setShowClinicFilter(true);
      },
      () => setShowClinicFilter(false)
    );
  };

  useEffect(() => {
    fetchData();
    fetchClinics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [selectedClinicCode, selectedStatus, selectedScanType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClinicChange = (e) => {
    const selectedClinicName = e.target.value;
    setSelectedClinic(selectedClinicName);

    if (!selectedClinicName) {
      setSelectedClinicCode('');
      return;
    }

    const selectedClinicData = clinics.find((clinic) => clinic.clinicName === selectedClinicName);
    setSelectedClinicCode(getClinicCode(selectedClinicData) || defaultClinicCode);
  };

  const filteredScans = useMemo(() => {
    if (!searchTerm) return scans;
    const term = searchTerm.toLowerCase();
    return scans.filter(
      (scan) =>
        scan.patientPhone.toLowerCase().includes(term) ||
        scan.scanType.toLowerCase().includes(term) ||
        scan.clinicCode.toLowerCase().includes(term) ||
        scan.resultSummary.toLowerCase().includes(term)
    );
  }, [scans, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredScans.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageScans = filteredScans.slice(pageStart, pageStart + PAGE_SIZE);
  const showingFrom = filteredScans.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + PAGE_SIZE, filteredScans.length);
  const totalUtilisedCredits = filteredScans.reduce((sum, scan) => {
    const value = Number(scan.creditsCharged);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [1];
    if (currentPage > 3) pages.push('…');
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i += 1) {
      pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push('…');
    pages.push(totalPages);
    return pages;
  };

  return (
    <div className="pl-page">
      <div className="pl-tabs">
        <button
          type="button"
          className={`pl-tab ${activeTab === 'patients' ? 'active' : ''}`}
          onClick={() => setActiveTab('patients')}
        >
          <Users size={16} />
          User Listing
        </button>
        <button
          type="button"
          className={`pl-tab ${activeTab === 'credits' ? 'active' : ''}`}
          onClick={() => setActiveTab('credits')}
        >
          <Database size={16} />
          Credits & Ledger
        </button>
      </div>

      {activeTab === 'credits' ? (
        <CreditsLedger clinicCode={selectedClinicCode || defaultClinicCode} />
      ) : (
        <div className="pl-card">
          <div className="pl-card-header">
            <div>
              <h2>User Listing</h2>
              <p>List of users and analysis they have taken</p>
              {!loading && !error && (
                <p className="pl-utilised-credits">
                  Utilised credits: <strong>{totalUtilisedCredits}</strong>
                </p>
              )}
            </div>
            <div className="pl-header-actions">
              <div className="pl-search">
                <Search size={15} />
                <input
                  type="text"
                  placeholder="Search phone or clinic..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <button
                type="button"
                className={`pl-filter-btn ${showFilters ? 'active' : ''}`}
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <Filter size={15} />
                Filter
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="pl-filters-row">
              {showClinicFilter && (
                <select value={selectedClinic} onChange={handleClinicChange}>
                  <option value="">All clinics</option>
                  {clinics.map((clinic, index) => (
                    <option key={index} value={clinic.clinicName}>
                      {clinic.clinicName}
                    </option>
                  ))}
                </select>
              )}
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                {statusFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={selectedScanType}
                onChange={(e) => setSelectedScanType(e.target.value)}
              >
                {scanTypeFilterOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {loading ? (
            <div className="pl-table-skeleton" aria-hidden="true">
              <div className="pl-table-skeleton-head">
                {[1, 2, 3, 4, 5].map((i) => (
                  <span key={i} className="skeleton skeleton-line sm" />
                ))}
              </div>
              {[1, 2, 3, 4, 5, 6].map((row) => (
                <div key={row} className="pl-table-skeleton-row">
                  <span className="skeleton skeleton-line md" />
                  <span className="skeleton skeleton-pill" />
                  <div className="pl-skeleton-date">
                    <span className="skeleton skeleton-line sm" />
                    <span className="skeleton skeleton-line xs" />
                  </div>
                  <span className="skeleton skeleton-line xs" />
                  <span className="skeleton skeleton-pill status" />
                </div>
              ))}
              <div className="pl-footer-skeleton">
                <span className="skeleton skeleton-line sm" />
                <div className="pl-pagination-skeleton">
                  {[1, 2, 3].map((i) => (
                    <span key={i} className="skeleton skeleton-square" />
                  ))}
                </div>
              </div>
            </div>
          ) : error ? (
            <div className="pl-error">
              <h3>Unable to load users</h3>
              <p>{error}</p>
            </div>
          ) : filteredScans.length === 0 ? (
            <div className="pl-empty">
              <Users size={28} />
              <h3>No users found</h3>
              <p>Try adjusting your filters or search terms</p>
            </div>
          ) : (
            <>
              <div className="pl-table-wrap">
                <table className="pl-table">
                  <thead>
                    <tr>
                      <th>Contact</th>
                      <th>Analysis</th>
                      <th>Date</th>
                      <th>Credits</th>
                      <th>Status</th>
                      <th aria-label="Open" />
                    </tr>
                  </thead>
                  <tbody>
                    {pageScans.map((scan) => (
                        <tr key={scan.id} onClick={() => setSelectedScan(scan)}>
                          <td>
                            <span className="pl-phone">{scan.patientPhone}</span>
                          </td>
                          <td>
                            <span className={getAnalysisBadgeClass(scan.scanType)}>
                              <Smile size={12} />
                              {formatAnalysisLabel(scan.scanType)}
                            </span>
                          </td>
                          <td>
                            <div className="pl-date-cell">
                              <span className="pl-date-primary">{scan.datePart}</span>
                              <span className="pl-date-secondary">{scan.timePart}</span>
                            </div>
                          </td>
                          <td>
                            <span className="pl-credits">{scan.creditsCharged}</span>
                          </td>
                          <td>
                            <span className={getStatusClass(scan.status)}>
                              {formatStatusLabel(scan.status)}
                            </span>
                          </td>
                          <td className="pl-chevron-cell">
                            <ChevronRight size={16} />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              <div className="pl-footer">
                <span className="pl-results-text">
                  Showing {showingFrom}–{showingTo} of {filteredScans.length}
                </span>
                <div className="pl-pagination">
                  <button
                    type="button"
                    className="pl-page-btn"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {getPageNumbers().map((item, idx) =>
                    item === '…' ? (
                      <span key={`ellipsis-${idx}`} className="pl-page-ellipsis">
                        …
                      </span>
                    ) : (
                      <button
                        type="button"
                        key={item}
                        className={`pl-page-btn ${currentPage === item ? 'active' : ''}`}
                        onClick={() => setPage(item)}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    type="button"
                    className="pl-page-btn"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {selectedScan && (
        <ScanDetailPanel scan={selectedScan} onClose={() => setSelectedScan(null)} />
      )}
    </div>
  );
};

export default Scans;
